import { Box } from '@chakra-ui/react'
import {
  GreenGreen10,
  GreenGreen20,
  GreenGreen60,
  IndigoIndigo10,
  IndigoIndigo20,
  IndigoIndigo50,
  PurplePurple10,
  PurplePurple20,
  PurplePurple60,
  RedRed10,
  RedRed20,
  RedRed60,
  YellowYellow10,
  YellowYellow20,
  YellowYellow60
} from '@sequencehq/design-tokens'
import { useAtom } from 'jotai'
import {
  ProductTimeline,
  productTimelinesAtom,
  progressOffsetAtom
} from '../atoms/timeline.atom'
import {
  BILLING_PERIOD_PILL_OFFSET_FROM_DATE_PX,
  TIMELINE_PRODUCT_ROW_HEIGHT_PX,
  TIMELINE_DATA_SERIES_Z_INDEX
} from '../timeline.constants'
import { useTimelineDimensions } from '../hooks/useTimelineDimensions'
import {
  BillingPeriodPill,
  BillingPeriodPillTheme,
  getProgressFillWidth
} from './BillingPeriodPill'
import { productsToHighlightAtom } from '../atoms/timelineUi.atom'

const ProductSeries = (props: {
  productTimeline: ProductTimeline
  timelineWidth: number
  productId: string
  theme: BillingPeriodPillTheme
  progressOffset: number
}) => {
  const [productsToHighlight] = useAtom(productsToHighlightAtom)
  const openEndedStartDateOffset = props.productTimeline.openEnded.startOffset

  const highlightThisProduct =
    !productsToHighlight || productsToHighlight.includes(props.productId)

  return (
    <Box
      width={`${props.timelineWidth}px`}
      height={`${TIMELINE_PRODUCT_ROW_HEIGHT_PX}px`}
      zIndex={TIMELINE_DATA_SERIES_Z_INDEX}
      position="relative"
      opacity={highlightThisProduct ? 1 : 0.5}
      transition={'opacity 0.2s ease-in-out'}
    >
      {props.productTimeline.billingPeriods.map(billingPeriod => {
        if (billingPeriod.proratedPrice) {
          /**
           * The reason we do this here, and not at a deeper modelling level, is
           * because the choice to visualize proration as two pills is a UI
           * choice and one of a few options! Therefore, by keeping the data model
           * clean of the opinion around how to visualize the proration and just
           * focusing on the billing periods itself, we keep things reasonably
           * simple!
           */
          const firstPillOffsets = {
            start:
              billingPeriod.startOffset +
              BILLING_PERIOD_PILL_OFFSET_FROM_DATE_PX,
            end:
              billingPeriod.proratedPrice.startDateOffset -
              BILLING_PERIOD_PILL_OFFSET_FROM_DATE_PX
          }

          const secondPillOffsets = {
            start:
              billingPeriod.proratedPrice.startDateOffset +
              BILLING_PERIOD_PILL_OFFSET_FROM_DATE_PX,
            end: billingPeriod.endOffset
          }

          return (
            <>
              <BillingPeriodPill
                key={`product-period-${billingPeriod.start.toString()}-1`}
                theme={props.theme}
                progressFillWidth={getProgressFillWidth({
                  startOfPeriodOffset: firstPillOffsets.start,
                  endOfPeriodOffset: firstPillOffsets.end,
                  progressOffset: props.progressOffset
                })}
                openEndedFillWidth={
                  openEndedStartDateOffset &&
                  Math.max(firstPillOffsets.end - openEndedStartDateOffset, 0)
                }
                isInAdvance={billingPeriod.inAdvance}
                isProrated
                width={firstPillOffsets.end - firstPillOffsets.start}
                offset={firstPillOffsets.start}
              />
              <BillingPeriodPill
                key={`product-period-${billingPeriod.start.toString()}-2`}
                theme={props.theme}
                progressFillWidth={getProgressFillWidth({
                  startOfPeriodOffset: secondPillOffsets.start,
                  endOfPeriodOffset: secondPillOffsets.end,
                  progressOffset: props.progressOffset
                })}
                isProrated
                openEndedFillWidth={
                  openEndedStartDateOffset &&
                  Math.max(secondPillOffsets.end - openEndedStartDateOffset, 0)
                }
                width={secondPillOffsets.end - secondPillOffsets.start}
                offset={secondPillOffsets.start + 1}
              />
            </>
          )
        }

        const pillStartOffset =
          billingPeriod.startOffset + BILLING_PERIOD_PILL_OFFSET_FROM_DATE_PX
        const pillEndOffset = billingPeriod.endOffset
        return (
          <BillingPeriodPill
            key={`product-period-${billingPeriod.start.toString()}`}
            theme={props.theme}
            progressFillWidth={getProgressFillWidth({
              startOfPeriodOffset: pillStartOffset,
              endOfPeriodOffset: pillEndOffset,
              progressOffset: props.progressOffset
            })}
            openEndedFillWidth={
              openEndedStartDateOffset &&
              Math.max(pillEndOffset - openEndedStartDateOffset, 0)
            }
            isInAdvance={billingPeriod.inAdvance}
            width={(pillEndOffset ?? 0) - pillStartOffset}
            offset={pillStartOffset + 0.5}
          />
        )
      })}
    </Box>
  )
}

const SERIES_COLOR_MAP: Record<string, BillingPeriodPillTheme> = {
  '0': {
    border: RedRed60,
    progressFill: RedRed20,
    upcomingFill: RedRed10
  },
  '1': {
    border: IndigoIndigo50,
    progressFill: IndigoIndigo20,
    upcomingFill: IndigoIndigo10
  },
  '2': {
    border: PurplePurple60,
    progressFill: PurplePurple20,
    upcomingFill: PurplePurple10
  },
  '3': {
    border: YellowYellow60,
    progressFill: YellowYellow20,
    upcomingFill: YellowYellow10
  },
  '4': {
    border: GreenGreen60,
    progressFill: GreenGreen20,
    upcomingFill: GreenGreen10
  }
}

export const ScheduleTimelineProducts = () => {
  const [productTimelines] = useAtom(productTimelinesAtom)
  const { timelineWidth } = useTimelineDimensions()
  const [progressOffset] = useAtom(progressOffsetAtom)

  return Object.entries(productTimelines).map(
    ([productId, productTimeline], idx) => (
      <ProductSeries
        key={productId}
        productTimeline={productTimeline}
        timelineWidth={timelineWidth}
        productId={productId}
        theme={SERIES_COLOR_MAP[`${idx % 5}`]}
        progressOffset={progressOffset}
      />
    )
  )
}
